import { Component } from '@theme/component';

/**
 * @typedef {Object} FloatingNavRefs
 * @property {HTMLElement} pill - The floating pill element.
 */

/**
 * Minimal floating navbar.
 *
 * Replaces `header-component` for the floating-nav section. Its only JS job is to
 * keep `--header-height` / `--header-group-height` in sync, because other sections
 * consume those vars for their offsets. Sticky behaviour is pure CSS (fixed pill),
 * so there is no scroll handling here.
 *
 * @extends {Component<FloatingNavRefs>}
 */
class FloatingNav extends Component {
  requiredRefs = ['pill'];

  #resizeObserver = new ResizeObserver(([entry]) => {
    if (!entry) return;
    const height = Math.round(entry.borderBoxSize?.[0]?.blockSize ?? this.refs.pill.offsetHeight);
    this.#setHeaderHeight(height);
  });

  connectedCallback() {
    super.connectedCallback();

    // Set immediately to avoid layout shift before the observer first fires.
    const height = this.refs.pill?.offsetHeight ?? 0;
    if (height) this.#setHeaderHeight(height);

    if (this.refs.pill) this.#resizeObserver.observe(this.refs.pill);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver.disconnect();
  }

  /** @param {number} height */
  #setHeaderHeight(height) {
    document.body.style.setProperty('--header-height', `${height}px`);
    document.body.style.setProperty('--header-group-height', `${height}px`);
  }
}

if (!customElements.get('floating-nav')) {
  customElements.define('floating-nav', FloatingNav);
}

/**
 * Cart-drawer wiring (ported from the previous header.liquid inline script).
 *
 * `header-actions` renders the actual <cart-drawer-component>. On mobile the
 * drawer needs to live on <body> to get real dimensions, and after Add-to-Cart
 * the section morph can re-insert a duplicate that must be cleaned up. The
 * visible trigger lives in the floating-nav pill (#custom-cart-icon /
 * [data-open-cart-drawer]).
 */
(() => {
  if (window.__floatingNavCartBound) return;
  window.__floatingNavCartBound = true;

  const getBagCounters = () => Array.from(document.querySelectorAll('[data-bag-count]'));
  const setCount = (value) => {
    const nextValue = value >= 0 ? value : 0;
    getBagCounters().forEach((counter) => {
      counter.textContent = String(nextValue);
    });
  };

  const syncFromSessionStorage = () => {
    const sessionStorageCount = sessionStorage.getItem('cart-count');
    if (!sessionStorageCount) return;
    try {
      const { value, timestamp } = JSON.parse(sessionStorageCount);
      if (Date.now() - timestamp < 10000) {
        const parsed = parseInt(value, 10);
        if (!Number.isNaN(parsed) && parsed >= 0) setCount(parsed);
      }
    } catch (_) {}
  };

  const bind = () => {
    // Move cart-drawer-component to body so it renders properly on mobile.
    const cartDrawerEl = document.querySelector('cart-drawer-component');
    if (cartDrawerEl && cartDrawerEl.parentElement !== document.body) {
      document.body.appendChild(cartDrawerEl);
    }

    // After Add to Cart, the section morph may re-insert a fresh
    // cart-drawer-component into header-actions. Transfer its items to the
    // body-level drawer and remove the duplicate.
    const headerActions = document.querySelector('header-actions');
    if (headerActions && cartDrawerEl) {
      new MutationObserver((mutations) => {
        for (const mutation of mutations) {
          for (const node of mutation.addedNodes) {
            if (!(node instanceof Element)) continue;
            if (node.tagName !== 'CART-DRAWER-COMPONENT') continue;

            const bodyDrawer = document.body.querySelector(':scope > cart-drawer-component');
            if (!bodyDrawer || node === bodyDrawer) continue;

            const freshCartItems = node.querySelector('cart-items-component');
            const existingCartItems = bodyDrawer.querySelector('cart-items-component');
            if (freshCartItems && existingCartItems) {
              existingCartItems.replaceWith(freshCartItems);
            }

            node.remove();
          }
        }
      }).observe(headerActions, { childList: true });
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bind, { once: true });
  } else {
    bind();
  }

  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('#custom-cart-icon, #custom-cart-icon-mobile, [data-open-cart-drawer]');
    if (!trigger) return;

    event.preventDefault();

    // Always use the body-level cart-drawer-component.
    const cartDrawer = document.body.querySelector(':scope > cart-drawer-component');

    if (cartDrawer) {
      if (typeof cartDrawer.open === 'function') {
        cartDrawer.open();
      } else {
        customElements.whenDefined('cart-drawer-component').then(() => {
          document.body.querySelector(':scope > cart-drawer-component')?.open();
        });
      }
      return;
    }

    window.location.href = '/cart';
  });

  document.addEventListener('cart:update', (event) => {
    // `itemCount` is the authoritative cart total, normalized at the producer.
    // Set it directly — no delta math.
    setCount(event.detail?.data?.itemCount ?? 0);
  });

  window.addEventListener('pageshow', (event) => {
    if (event.persisted) syncFromSessionStorage();
  });

  syncFromSessionStorage();
})();
